/* eslint-disable unused-imports/no-unused-vars */
import { Observable, Subject } from 'rxjs';
import { AjaxError, AjaxRequest } from 'rxjs/ajax';
import { filter, map, timeout } from 'rxjs/operators';
import { CimStreamOptions, CimStreamResponse } from '../data/cim-stream';
import { headerConstants, HttpStatusCode } from '../data/http-constants';
import { Net } from '../data/net';
import { PowerShellStreamResponse } from '../data/powershell-stream';

/**
 * Defines an signed http request token
 */
export interface SignedHttpRequestToken {
    /**
     * JSON Web Token (string).
     */
    jwt: string;

    /**
     * User name.
     */
    username: string;
}

export interface SignOnTokenResponse {
    /**
     * The time aquired.
     */
    time: number;

    /**
     * The signed http request token.
     */
    token: SignedHttpRequestToken;
}

export interface SignOnTokenRefresh {
    /**
     * The time aquired.
     */
    time: number;
}

/**
 * Manage Signed HTTP Request token (Proof of possession token) handling.
 */
export class SignOnManager {
    /**
     * Wait for one minute to respond by the portal.
     */
    private portalMaxWaitTimeout = 60 * 1000;

    /**
     * The PoP Authorization Token.
     */
    public signedHttpRequestToken: SignedHttpRequestToken;

    /**
     * The sign on token awaiter subject.
     */
    public signOnTokenAwaiter: Subject<SignOnTokenResponse>;

    /**
     * The sign on token requester subject.
     */
    private signOnTokenRefresh: Subject<SignOnTokenRefresh>;

    /**
     * The refresh request to the authorization manager.
     */
    private refreshCall: () => Observable<SignOnTokenResponse>;

    /**
     * Initializes a new instance of the SignOnManager class.
     */
    constructor(refreshCall: () => Observable<SignOnTokenResponse>) {
        this.refreshCall = refreshCall;
        if (MsftSme.isShell()) {
            this.signOnTokenAwaiter = new Subject<SignOnTokenResponse>();
            this.signOnTokenRefresh = new Subject<SignOnTokenRefresh>();
        }
    }

    /**
     * Gets a valude indicating whethere sign on token is active and configured.
     */
    public get isSignOnTokenEnabled(): boolean {
        return !!this.signedHttpRequestToken;
    }

    /**
     * Gets the sign on token request observable by Shell.
     */
    public get signOnTokenRefreshTarget(): Observable<SignOnTokenRefresh> {
        if (this.signOnTokenRefresh == null) {
            throw new Error('Request object is only for Shell.');
        }

        return this.signOnTokenRefresh.asObservable();
    }

    /**
     * Apply signed http request token.
     */
    public applySignedHttpRequestToken(token: SignedHttpRequestToken): void {
        if (token) {
            const time = Date.now();
            this.signedHttpRequestToken = token;
            this.signOnTokenAwaiter.next({ time, token });
        }
    }

    /**
     * Request sign on token to refresh.
     * @returns success void.
     */
    public requestSignOnRefresh(): Observable<SignOnTokenResponse> {
        const now = Date.now();
        const awaiter = this.signOnTokenAwaiter
            .pipe(
                filter(token => token.time > now),
                timeout(this.portalMaxWaitTimeout)
            );
        this.signOnTokenRefresh.next({ time: now });
        return awaiter;
    }

    /**
     * Check if it can handle unauthorized login case.
     * @param code the error code.
     * @param error the ajax error response.
     * @returns true if it can handle.
     */
    public canHandleUnauthorizedLogin(code: HttpStatusCode, error: AjaxError): boolean {
        // unauthorized login was observed.
        return Net.isUnauthorizedLogin(error);
    }

    /**
     * Handles the unauthorized login case.
     * @param code the error code.
     * @param request the ajax request.
     * @param error the ajex error reponse.
     * @returns success if it could handled.
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public handleUnauthorizedLogin(code: HttpStatusCode, request: AjaxRequest, error: AjaxError): Observable<AjaxRequest> {
        // request new Sign On token to the parent frame which is Azure Compute VM UI.
        return this.refreshCall()
            .pipe(map(() => {
                // update with newly aquired PoP jwt token.
                this.SetAadAuthorizationHeader(request.headers);
                return request;
            }));
    }

    /**
     * Check if it can handle unauthorized login case.
     * @param response the response of CIM stream query.
     */
    public canHandleStreamUnauthorizedLogin(response: CimStreamResponse): boolean {
        // access denied case.
        const responseObject = response && response.response;
        if (!responseObject) {
            return false;
        }

        return responseObject.statusCode === HttpStatusCode.Unauthorized && responseObject.forbidden && responseObject.forbidden === 'UnauthorizedLogin';
    }

    /**
     * Handle returns true for an ajax error, this method can be called to handle that error.
     */
    public handleStreamUnauthorizedLogin(
        options: CimStreamOptions, response: CimStreamResponse | PowerShellStreamResponse): Observable<CimStreamOptions> {
        let message = response.response.error && response.response.error.message;
        if (!message) {
            const errors = response.response.errors;
            if (errors && errors.length > 0) {
                message = errors[0].message;
            }
        }

        return this.refreshCall().pipe(map(() => options));
    }

    /**
     * Set AAD authorization header.
     * @param headers the headers.
     */
    public SetAadAuthorizationHeader(headers?: any): void {
        headers[headerConstants.SME_AAD_AUTHORIZATION] = `WAC;PAS ${this.signedHttpRequestToken.jwt}`;
    }
}
